import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Shield, Crown, Check, X, Ban, Eye, EyeOff, Download, Euro, Save, RotateCcw, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminLang } from '../../hooks/useAdminLang';
import { getMediaUrl } from '../../utils/mediaUrl';

export function GreenIARegistryAdminPanel() {
  const { at } = useAdminLang();
  const [data, setData] = useState({ items: [], total: 0 });
  const [apia, setApia] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get('/api/admin-v509/green-registry'),
        axios.get('/api/admin-v509/green-apia'),
      ]);
      setData(r1.data || { items: [], total: 0 });
      setApia(r2.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, approve) => {
    try {
      await axios.post(`/api/admin-v509/green-apia/${id}/decide`, { approve, reason: approve ? 'Vérifié' : 'Non conforme' });
      await load();
      toast.success(approve ? at('giaAdminIssued') : at('giaAdminRefused'));
    } catch { toast.error(at('giaAdminError')); }
  };

  const downloadCert = async (id, certNumber) => {
    try {
      const r = await axios.get(`/api/green-ia/ap-ia/${id}/certificate`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Green-APIA-${certNumber || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(at('giaAdminError')); }
  };

  const [viewer, setViewer] = useState(null); // legacy (unused)
  const [certUrl, setCertUrl] = useState(null);

  const openSampleCert = async () => {
    try {
      const r = await axios.get('/api/admin-v509/green-apia/sample-certificate', { responseType: 'blob' });
      setCertUrl(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })));
    } catch { toast.error(at('giaAdminError')); }
  };

  const downloadSampleCert = async () => {
    try {
      const r = await axios.get('/api/admin-v509/green-apia/sample-certificate', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Green-APIA-Certificat-Apercu.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(at('giaAdminError')); }
  };

  const downloadLabel = async () => {
    try {
      const r = await axios.get('/api/green-ia/ap-ia/label.svg', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Green-IA-Label.svg';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(at('giaAdminError')); }
  };

  return (
    <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30" data-testid="green-ia-admin">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-green-300" />
        <h4 className="font-bold text-green-200 text-sm">{at('giaAdminTitle')}</h4>
      </div>

      {/* Label & certificate preview + downloads (v50.8) */}
      <div className="mb-3 p-3 rounded-xl bg-black/40 border border-green-500/25" data-testid="green-ia-brand-assets">
        <div className="text-[11px] font-semibold text-green-200/80 mb-2">{at('giaAssetsTitle') || 'Label & certificat Green IA'}</div>
        <div className="flex items-center gap-3 flex-wrap">
          <img
            src={getMediaUrl('/api/green-ia/ap-ia/label.svg')}
            alt="Label Green IA"
            className="h-16 bg-black/60 rounded-lg p-1 border border-green-500/20"
            data-testid="green-ia-label-preview"
          />
          <div className="flex flex-col gap-1.5">
            <button onClick={downloadLabel} className="px-3 py-1.5 rounded-full bg-green-500/20 hover:bg-green-500/30 text-[11px] text-green-200 font-semibold flex items-center gap-1.5" data-testid="green-ia-label-download">
              <Download className="w-3 h-3" /> {at('giaLabelDl') || 'Télécharger le label (SVG)'}
            </button>
            <button onClick={openSampleCert} className="px-3 py-1.5 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-[11px] text-emerald-200 font-semibold flex items-center gap-1.5" data-testid="green-ia-cert-view">
              <Download className="w-3 h-3" /> {at('giaCertView') || 'Voir le certificat (aperçu)'}
            </button>
            <button onClick={downloadSampleCert} className="px-3 py-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/25 text-[11px] text-emerald-200/80 font-semibold flex items-center gap-1.5" data-testid="green-ia-cert-download">
              <Download className="w-3 h-3" /> {at('giaCertDl') || 'Télécharger le certificat (PDF)'}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-green-100/50 mt-2">{at('giaLabelI18nNote') || 'Le label est traduit automatiquement dans les 13 langues (?lang=xx) — il s\'affiche dans la langue du pro au moment de sa demande.'}</p>
      </div>

      {/* Certificate viewer modal with close button */}
      {certUrl && (
        <div className="fixed inset-0 bg-black/85 z-[10000] flex items-center justify-center p-4" data-testid="green-ia-cert-modal">
          <div className="bg-gray-900 border border-emerald-500/30 rounded-2xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
              <p className="text-sm font-bold text-emerald-200">{at('giaCertView') || 'Certificat Green AP-IA (aperçu)'}</p>
              <div className="flex gap-2">
                <button onClick={downloadSampleCert} className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-[11px] text-emerald-200 font-semibold" data-testid="green-ia-cert-modal-download">PDF ↓</button>
                <button onClick={() => { URL.revokeObjectURL(certUrl); setCertUrl(null); }} className="px-3 py-1.5 rounded-full bg-red-500/20 text-[11px] text-red-200 font-semibold" data-testid="green-ia-cert-modal-close">✕ {at('giaClose') || 'Fermer'}</button>
              </div>
            </div>
            <iframe src={certUrl} title="certificat" className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="p-2 rounded-xl bg-black/40 border border-green-500/20">
              <div className="text-[10px] text-green-200/60">{at('giaAdminProtected')}</div>
              <div className="text-lg font-bold text-white">{data.total}</div>
            </div>
            <div className="p-2 rounded-xl bg-black/40 border border-green-500/20">
              <div className="text-[10px] text-green-200/60">{at('giaAdminApiaReqs')}</div>
              <div className="text-lg font-bold text-white">{apia.length}</div>
            </div>
          </div>
          {apia.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-green-200/80 mb-1">{at('giaAdminReqSubtitle')}</div>
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {apia.map(a => (
                  <div key={a.id} className={`p-2 rounded-lg border ${a.status === 'approved' ? 'bg-green-500/10 border-green-500/40' : a.status === 'rejected' ? 'bg-red-500/10 border-red-500/40' : 'bg-black/30 border-green-500/20'}`}>
                    <div className="text-xs font-semibold text-white">{a.company_name} · <span className="text-green-200">{a.tool_or_app_name}</span></div>
                    <div className="text-[10px] text-green-200/60 truncate">{a.country} · {a.contact_email}</div>
                    {a.certificate_number && (
                      <div className="mt-1 flex items-center gap-1.5">
                        <div className="text-[10px] text-green-300 font-mono">{at('giaAdminCert')}: {a.certificate_number}</div>
                        <button onClick={() => downloadCert(a.id, a.certificate_number)} className="ml-auto p-1 rounded-full bg-green-500/20 hover:bg-green-500/30" title="PDF" data-testid={`gia-cert-dl-${a.id}`}>
                          <Download className="w-3 h-3 text-green-200" />
                        </button>
                      </div>
                    )}
                    {a.status === 'pending' && (
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => decide(a.id, true)} className="flex-1 py-1 rounded-full bg-green-500 text-[10px] font-semibold flex items-center justify-center gap-1" data-testid={`gia-approve-${a.id}`}><Check className="w-3 h-3" /> {at('giaAdminApprove')}</button>
                        <button onClick={() => decide(a.id, false)} className="flex-1 py-1 rounded-full bg-red-500 text-[10px] font-semibold flex items-center justify-center gap-1" data-testid={`gia-reject-${a.id}`}><X className="w-3 h-3" /> {at('giaAdminReject')}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function SubscriptionsAdminPanel() {
  const { at } = useAdminLang();
  const [enabled, setEnabled] = useState(false);
  const [data, setData] = useState({ items: [], counts: {}, revenue_estimated: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        axios.get('/api/subscription/settings', { params: { lang: 'fr' } }).then(r => r.data),
        axios.get('/api/admin-v509/subscriptions').then(r => r.data),
      ]);
      setEnabled(!!s.enabled);
      setData(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    try {
      const r = await axios.post('/api/admin-v509/subscription-enabled', { enabled: !enabled });
      setEnabled(!!r.data.enabled);
      toast.success(r.data.enabled ? at('subAdminOnMsg') : at('subAdminOffMsg'));
    } catch { toast.error(at('subAdminError')); }
  };

  const cancelSub = async (id) => {
    if (!window.confirm(at('subAdminCancelConfirm'))) return;
    try {
      await axios.post(`/api/admin-v509/subscriptions/${id}/cancel`);
      await load();
    } catch { toast.error(at('subAdminError')); }
  };

  return (
    <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-yellow-500/30" data-testid="subscriptions-admin">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-yellow-300" />
          <h4 className="font-bold text-yellow-200 text-sm">{at('subAdminTitle')}</h4>
        </div>
        <button onClick={toggle} className={`px-3 py-1.5 rounded-full font-semibold text-[11px] flex items-center gap-1.5 ${enabled ? 'bg-yellow-500 text-black' : 'bg-white/10 text-white'}`} data-testid="sub-master-toggle">
          {enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {enabled ? at('subAdminEnabled') : at('subAdminDisabled')}
        </button>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <StatCell label={at('subAdminTotal')} value={data.counts?.total || 0} />
            <StatCell label={at('subAdminActive')} value={data.counts?.active || 0} accent="text-green-300" />
            <StatCell label={at('subAdminPending')} value={data.counts?.pending || 0} />
            <StatCell label={at('subAdminCancelled')} value={data.counts?.cancelled || 0} />
          </div>
          <div className="p-2 rounded-xl bg-yellow-500/20 border border-yellow-500/40 text-center mb-3">
            <div className="text-[10px] text-yellow-200/70">{at('subAdminRevenue')}</div>
            <div className="text-lg font-bold text-yellow-100">{(data.revenue_estimated || 0).toFixed(2)} €</div>
          </div>
          <SubscriptionPricingEditor />
          <GiftSubscriptionEditor onGranted={load} />
          {data.items?.length > 0 ? (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {data.items.slice(0, 30).map(s => (
                <div key={s.id} className="p-2 rounded-lg bg-black/30 border border-yellow-500/20 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-white truncate">{s.user_email || s.user_id}</div>
                    <div className="text-[10px] text-yellow-200/60">{s.plan} · {s.currency} {s.amount} · <span className={s.status === 'active' ? 'text-green-300' : 'text-white/60'}>{s.status}</span></div>
                  </div>
                  {s.status === 'active' && (
                    <button onClick={() => cancelSub(s.id)} className="p-1.5 rounded-full bg-red-500/20 hover:bg-red-500/30" data-testid={`sub-cancel-${s.id}`}>
                      <Ban className="w-3 h-3 text-red-300" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : <div className="text-[11px] text-yellow-200/50 text-center py-2">{at('subAdminEmpty')}</div>}
        </>
      )}
    </div>
  );
}

/** Admin — editable Premium prices (EUR base; other currencies scale automatically). Applied app-wide instantly. */
function SubscriptionPricingEditor() {
  const [prices, setPrices] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    axios.get('/api/admin-v509/subscription-pricing').then(r => {
      setPrices(r.data.prices); setDefaults(r.data.defaults); setUpdatedAt(r.data.updated_at);
    }).catch(() => setPrices(null));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { monthly: parseFloat(prices.monthly), yearly: parseFloat(prices.yearly), credits_20: parseFloat(prices.credits_20) };
      const r = await axios.post('/api/admin-v509/subscription-pricing', body);
      setPrices(r.data.prices);
      setUpdatedAt(new Date().toISOString());
      toast.success('Prix Premium mis à jour — appliqués dans toute l\u2019app');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\u2019enregistrement'); }
    finally { setSaving(false); }
  };

  if (!prices) return null;
  const fields = [['monthly', 'Mensuel / mois'], ['yearly', 'Annuel / an'], ['credits_20', 'Pack crédits (100 crédits)']];
  return (
    <div className="p-3 rounded-xl bg-black/30 border border-yellow-500/30 mb-3" data-testid="sub-pricing-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-yellow-200"><Euro className="w-3.5 h-3.5" /> Prix Premium (base EUR)</div>
        <div className="flex items-center gap-1">
          {defaults && <button onClick={() => setPrices({ ...defaults })} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10" title="Valeurs par défaut" data-testid="sub-price-reset"><RotateCcw className="w-3.5 h-3.5" /></button>}
          <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-full bg-yellow-500 text-black text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="sub-price-save">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {fields.map(([k, label]) => (
          <label key={k} className="flex flex-col gap-1 text-[10px] text-yellow-200/70">
            {label}
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-black/40 border border-yellow-500/30">
              <input type="number" step="0.01" min="0.5" value={prices[k]} onChange={(e) => setPrices(p => ({ ...p, [k]: e.target.value }))} className="w-full bg-transparent text-sm font-bold text-white focus:outline-none" data-testid={`sub-price-${k}`} />
              <span className="text-yellow-300 text-xs">€</span>
            </div>
          </label>
        ))}
      </div>
      <p className="text-[10px] text-yellow-200/50 mt-2">Les autres devises (USD, GBP, BRL, INR…) sont recalculées automatiquement. Appliqué immédiatement : onglet Déesse › Abonnement Premium, paiement Stripe, comptabilité.{updatedAt ? ` · Dernière modification : ${new Date(updatedAt).toLocaleString()}` : ''}</p>
    </div>
  );
}

export function VideoToliStatsPanel() { return null; /* deprecated in v50.14 — replaced by Photos partagées */ }

/** Admin — offer free Premium (monthly/yearly) or free credits to a user. Visible instantly in the user's Premium panel + Settings. */
function GiftSubscriptionEditor({ onGranted }) {
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState('monthly');
  const [credits, setCredits] = useState(20);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHist, setShowHist] = useState(false);

  const loadHist = async () => {
    try { const r = await axios.get('/api/admin-v509/subscriptions/gift/history', { params: { limit: 30 } }); setHistory(r.data?.items || []); } catch { /* ignore */ }
  };

  const grant = async () => {
    const target = (email || '').trim();
    if (!target) { toast.error('Email requis'); return; }
    if (kind === 'credits' && (!credits || credits < 1)) { toast.error('Crédits > 0'); return; }
    setBusy(true);
    try {
      const body = { email: target, kind, note: note.trim() || undefined };
      if (kind === 'credits') body.credits = parseInt(credits, 10);
      const r = await axios.post('/api/admin-v509/subscriptions/gift', body);
      toast.success(kind === 'credits' ? `${r.data.credits} crédits offerts à ${r.data.user_email}` : `Premium ${kind} offert à ${r.data.user_email}`);
      setEmail(''); setNote(''); await loadHist();
      onGranted?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 rounded-xl bg-black/30 border border-fuchsia-500/30 mb-3" data-testid="sub-gift-editor">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-200 mb-2"><Gift className="w-3.5 h-3.5" /> Offrir un cadeau (Premium ou crédits, gratuit)</div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email de l&rsquo;utilisateur" className="px-3 py-1.5 rounded-lg bg-black/40 border border-fuchsia-500/30 text-xs text-white focus:outline-none focus:border-fuchsia-400" data-testid="sub-gift-email" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="px-2 py-1.5 rounded-lg bg-black/40 border border-fuchsia-500/30 text-xs text-white" data-testid="sub-gift-kind">
          <option value="monthly">Premium 1 mois</option>
          <option value="yearly">Premium 1 an</option>
          <option value="credits">Crédits (doublage)</option>
        </select>
        {kind === 'credits' ? (
          <input type="number" min="1" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder="Nombre de crédits" className="px-3 py-1.5 rounded-lg bg-black/40 border border-fuchsia-500/30 text-xs text-white focus:outline-none focus:border-fuchsia-400" data-testid="sub-gift-credits" />
        ) : (
          <div className="text-[11px] text-fuchsia-200/60 self-center pl-1">Accès Premium gratuit, sans Stripe.</div>
        )}
        <button onClick={grant} disabled={busy || !email} className="px-3 py-1.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-[11px] font-semibold flex items-center justify-center gap-1 disabled:opacity-40" data-testid="sub-gift-submit">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />} Offrir
        </button>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (optionnel)" className="w-full px-3 py-1.5 rounded-lg bg-black/40 border border-fuchsia-500/30 text-[11px] text-white/80 mb-2 focus:outline-none focus:border-fuchsia-400" data-testid="sub-gift-note" />
      <button onClick={async () => { const next = !showHist; setShowHist(next); if (next && history.length === 0) await loadHist(); }} className="text-[11px] text-fuchsia-300 hover:text-fuchsia-200 underline" data-testid="sub-gift-history-toggle">
        {showHist ? 'Masquer l\u2019historique des cadeaux' : 'Voir l\u2019historique des cadeaux'}
      </button>
      {showHist && (
        <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="sub-gift-history">
          {history.length === 0 ? (
            <p className="text-[11px] text-fuchsia-200/50">Aucun cadeau offert pour le moment.</p>
          ) : history.map((g) => (
            <div key={g.id} className="p-1.5 rounded-lg bg-black/40 border border-fuchsia-500/20 text-[11px] text-white flex items-center justify-between gap-2">
              <span className="truncate">{g.user_email || g.user_id}</span>
              <span className="text-fuchsia-200/80">
                {g.plan === 'gift_credits' ? `${g.credits} crédits` : `Premium ${g.plan}`}
                {g.note ? ` · ${g.note}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCell = ({ label, value, accent = 'text-white' }) => (
  <div className="p-2 rounded-xl bg-black/40 border border-white/10 text-center">
    <div className="text-[10px] text-white/60">{label}</div>
    <div className={`text-sm font-bold ${accent}`}>{value?.toLocaleString?.() || value || 0}</div>
  </div>
);
